import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "@/components/MarkerClusterGroup";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Search } from "lucide-react";

const EVENT_KINDS = ["wildfire", "flood", "river_overflow", "landslide", "storm", "hailstorm", "tornado", "heavy_snow", "avalanche", "sea_storm", "heatwave", "coldwave"];

export default function EventsHistoryPage() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [country, setCountry] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Icon created inside the component (module-scope Leaflet DivIcons get frozen
  // by the bundler/strict-mode and then MarkerClusterGroup fails to stamp
  // _leaflet_id on them).
  const eventMarker = useMemo(() => new L.DivIcon({
    className: "",
    html: '<div style="width:24px;height:24px;background:#6B7280;border:3px solid #fff;border-radius:50%;box-shadow:0 4px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px">!</div>',
    iconSize: [24, 24], iconAnchor: [12, 12],
  }), []);

  const load = async () => {
    try {
      const params = { history: true, limit: 500 };
      if (kind && kind !== "all") params.kind = kind;
      if (country) params.country_code = country;
      const { data } = await api.get("/events", { params });
      setItems(data || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); }, [kind, country]);

  const filtered = useMemo(() => items.filter((e) => {
    const s = q.toLowerCase();
    if (s && !(e.title || "").toLowerCase().includes(s) && !(e.description || "").toLowerCase().includes(s) && !(e.province_name || "").toLowerCase().includes(s)) return false;
    if (dateFrom && new Date(e.created_at) < new Date(dateFrom)) return false;
    if (dateTo && new Date(e.created_at) > new Date(dateTo)) return false;
    return true;
  }), [items, q, dateFrom, dateTo]);

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="events-history-page">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton testid="events-history-back" /></div>
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-600 font-semibold mb-1 flex items-center gap-2"><History size={14} /> {t("events_history_kicker")}</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">Eventi conclusi</h1>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-4 grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Cerca</label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input data-testid="history-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Titolo, descrizione, provincia…" className="rounded-full pl-9 mt-1" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Categoria</label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger data-testid="history-kind" className="mt-1 rounded-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {EVENT_KINDS.map((k) => <SelectItem key={k} value={k}>{t(`event_kind_${k}`) || k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Paese (ISO2)</label>
            <Input data-testid="history-country" value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} placeholder="IT, FR, DE…" maxLength={2} className="rounded-full mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Dal</label>
            <Input data-testid="history-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-full mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Al</label>
            <Input data-testid="history-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-full mt-1" />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden h-[420px]" data-testid="history-map">
            <MapContainer center={[42, 12]} zoom={4} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
              <MarkerClusterGroup
                reports={filtered
                  .filter((e) => e.centroid && typeof e.centroid.lat === "number" && typeof e.centroid.lng === "number")
                  .map((e) => ({ ...e, lat: e.centroid.lat, lng: e.centroid.lng, report_id: e.event_id }))}
                buildMarker={(e) => {
                  const m = L.marker([e.lat, e.lng], { icon: eventMarker });
                  const label = t(`event_kind_${e.kind}`) || e.kind;
                  const dateLocale = lang || undefined;
                  const startedStr = new Date(e.created_at).toLocaleDateString(dateLocale);
                  const endedStr = e.concluded_at ? new Date(e.concluded_at).toLocaleDateString(dateLocale) : "?";
                  m.bindPopup(
                    `<div style="font-size:12px">
                      <div style="font-weight:700">${(e.title || "").replace(/</g, "&lt;")}</div>
                      <div>${label}</div>
                      <div>${startedStr} → ${endedStr}</div>
                      <a href="/events/${e.event_id}" style="color:#047857;font-weight:600">${t("details_arrow")}</a>
                     </div>`
                  );
                  return m;
                }}
              />
            </MapContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-3 overflow-y-auto max-h-[420px]" data-testid="history-list">
            <div className="text-xs text-slate-500 font-semibold mb-2" data-testid="history-count">{filtered.length} {t("events_history_count_suffix")}</div>
            {filtered.length === 0 && <div className="text-sm text-slate-500 italic p-4 text-center">{t("events_history_none")}</div>}
            <div className="space-y-2">
              {filtered.map((e) => (
                <Link key={e.event_id} to={`/events/${e.event_id}`} className="block rounded-xl border border-slate-200 p-3 hover:bg-slate-50" data-testid={`history-item-${e.event_id}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 font-semibold">{t(`event_kind_${e.kind}`) || e.kind}</span>
                    <span className="text-slate-500">{e.province_name} · {e.province_country_code}</span>
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1">{e.title}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Inizio {new Date(e.created_at).toLocaleDateString()} · Fine {e.concluded_at ? new Date(e.concluded_at).toLocaleDateString() : "—"}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
